// src/pages/Blog.js
// WINTIME 筆記（Blog）— 部落格主檔
// 三個 view 切換：列表 / 單篇 / 編輯
// 管理員（sessionStorage.wintime_admin='1'）：可發文、編輯、刪除、隱藏留言、上傳圖片
// 訪客：可看已發布文章、可留言（暱稱必填）
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = process.env.REACT_APP_API_URL || 'https://stock-api-production-913a.up.railway.app';

// ════════════════════════ 樣式（沿用 silver-tech 風格）════════════════════════
const S = {
  page: { background: 'linear-gradient(180deg, #0b121a 0%, #080b10 100%)', minHeight: '100vh', padding: '24px 32px', color: '#cfe6f5' },
  title: { fontFamily: "'Rajdhani',sans-serif", fontSize: 32, fontWeight: 700, letterSpacing: 4, color: '#cfe6f5', marginBottom: 4 },
  subtitle: { fontFamily: "'Rajdhani',sans-serif", fontSize: 11, letterSpacing: 3, color: '#4a6878', fontWeight: 700, marginBottom: 24 },
  divider: { height: 1, background: 'linear-gradient(90deg, #1e3a52, transparent)', marginBottom: 20 },
  card: { background: 'linear-gradient(135deg, #0c1826, #0a1119)', border: '1px solid #1a2a3c', borderRadius: 8, padding: 20, marginBottom: 16, cursor: 'pointer', transition: 'border-color .2s' },
  cardTitle: { fontFamily: "'Rajdhani',sans-serif", fontSize: 22, fontWeight: 700, color: '#f5c518', marginBottom: 8 },  /* BLOG_CARDTITLE_V1 */
  cardMeta: { fontSize: 11, color: '#4a6878', fontFamily: "'Rajdhani',sans-serif", letterSpacing: 2, fontWeight: 700, marginBottom: 10 },
  cardExcerpt: { fontSize: 13, color: '#90c0dc', lineHeight: 1.7, fontFamily: "'Barlow',sans-serif" },
  cardCover: { width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 6, marginBottom: 12 },
  btn: (bg = '#2e5872', extra = {}) => ({ padding: '8px 20px', background: `linear-gradient(135deg, ${bg}, ${bg}cc)`, color: '#eef8ff', border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, fontSize: 12, letterSpacing: 2, marginRight: 8, ...extra }),
  input: { width: '100%', background: '#060910', border: '1px solid #2a4060', padding: '10px 14px', color: '#cfe6f5', fontSize: 14, outline: 'none', fontFamily: "'Barlow',sans-serif", borderRadius: 4, marginBottom: 12, boxSizing: 'border-box' },
  textarea: { width: '100%', background: '#060910', border: '1px solid #2a4060', padding: '12px 14px', color: '#cfe6f5', fontSize: 14, outline: 'none', fontFamily: "'JetBrains Mono', monospace", borderRadius: 4, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 },
  label: { color: '#90c0dc', fontSize: 10, letterSpacing: 3, fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, marginBottom: 6, display: 'block' },
  postTitle: { fontFamily: "'Rajdhani',sans-serif", fontSize: 34, fontWeight: 700, color: '#f5c518', marginBottom: 10, lineHeight: 1.3 },  /* BLOG_STYLE_V1 */
  postMeta: { fontSize: 11, color: '#4a6878', fontFamily: "'Rajdhani',sans-serif", letterSpacing: 2, fontWeight: 700, marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #1e2a3a' },
  postBody: { fontSize: 15, color: '#cfe6f5', lineHeight: 1.85, fontFamily: "'Barlow',sans-serif", marginBottom: 40 },
  commentBox: { background: '#0a1219', border: '1px solid #1a2a3c', borderRadius: 6, padding: 14, marginBottom: 12 },
  commentMeta: { fontSize: 11, color: '#7ab0cc', fontFamily: "'Rajdhani',sans-serif", letterSpacing: 1.5, fontWeight: 700, marginBottom: 6 },
  commentBody: { fontSize: 13, color: '#cfe6f5', lineHeight: 1.6, fontFamily: "'Barlow',sans-serif", whiteSpace: 'pre-wrap' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#4a6878', fontFamily: "'Rajdhani',sans-serif", fontSize: 14, letterSpacing: 2 },
};

// ════════════════════════ Markdown 渲染樣式 ════════════════════════
const mdComponents = {
  h1: ({ node, ...p }) => <h1 style={{ fontFamily: "'Rajdhani',sans-serif", fontSize: 26, color: '#cfe6f5', marginTop: 28, marginBottom: 14, fontWeight: 700 }} {...p} />,
  h2: ({ node, ...p }) => <h2 style={{ fontFamily: "'Rajdhani',sans-serif", fontSize: 22, color: '#cfe6f5', marginTop: 24, marginBottom: 12, fontWeight: 700 }} {...p} />,
  h3: ({ node, ...p }) => <h3 style={{ fontFamily: "'Rajdhani',sans-serif", fontSize: 18, color: '#90c0dc', marginTop: 20, marginBottom: 10, fontWeight: 700 }} {...p} />,
  p: ({ node, children, ...p }) => {  /* BLOG_STYLE_V1 首4字放大變色 */
    const kids = Array.isArray(children) ? children : [children];
    // 只在第一個 child 是純文字時才切前 4 字,否則原樣渲染(避免切壞連結/圖片)
    if (kids.length > 0 && typeof kids[0] === 'string' && kids[0].length > 0) {
      const head = kids[0].slice(0, 4);
      const tail = kids[0].slice(4);
      const rest = kids.slice(1);
      return <p style={{ marginBottom: 14, lineHeight: 1.85 }} {...p}>
        <span style={{ color: '#f5c518', fontSize: '1.3em', fontWeight: 700 }}>{head}</span>{tail}{rest}
      </p>;
    }
    return <p style={{ marginBottom: 14, lineHeight: 1.85 }} {...p}>{children}</p>;
  },
  a: ({ node, ...p }) => <a style={{ color: '#4C9BB8', textDecoration: 'underline' }} target="_blank" rel="noreferrer" {...p} />,
  strong: ({ node, ...p }) => <strong style={{ color: '#f5c518', fontWeight: 700 }} {...p} />,
  em: ({ node, ...p }) => <em style={{ color: '#7ab0cc' }} {...p} />,
  ul: ({ node, ...p }) => <ul style={{ paddingLeft: 24, marginBottom: 14 }} {...p} />,
  ol: ({ node, ...p }) => <ol style={{ paddingLeft: 24, marginBottom: 14 }} {...p} />,
  li: ({ node, ...p }) => <li style={{ marginBottom: 6, lineHeight: 1.7 }} {...p} />,
  blockquote: ({ node, ...p }) => <blockquote style={{ borderLeft: '3px solid #4C9BB8', paddingLeft: 14, margin: '14px 0', color: '#90c0dc', fontStyle: 'italic' }} {...p} />,
  code: ({ node, inline, ...p }) => inline
    ? <code style={{ background: '#1a2a3c', padding: '2px 6px', borderRadius: 3, fontFamily: "'JetBrains Mono',monospace", fontSize: 13, color: '#7ab0cc' }} {...p} />
    : <code style={{ display: 'block', background: '#060910', padding: 12, borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", fontSize: 13, color: '#7ab0cc', overflowX: 'auto', marginBottom: 14 }} {...p} />,
  img: ({ node, ...p }) => <img style={{ maxWidth: '100%', borderRadius: 6, marginBottom: 14 }} {...p} alt={p.alt || ''} />,
  hr: () => <hr style={{ border: 'none', borderTop: '1px solid #1e2a3a', margin: '24px 0' }} />,
};


// ════════════════════════ 工具：清除節錄中的 markdown 標記 ════════════════════════
const stripMarkdown = (md = '') => md
  .replace(/!\[.*?\]\(.*?\)/g, '')       // 圖片整段移除
  .replace(/\[(.*?)\]\(.*?\)/g, '$1')   // 連結保留文字
  .replace(/[#*_`>]/g, '')                  // 標題/粗體/斜體/code/引言符號
  .replace(/^[-*]\s+/gm, '')               // 列表符號
  .replace(/\n+/g, ' ')                    // 換行變空白
  .replace(/\s+/g, ' ')                    // 多空白變一個
  .trim();

// ════════════════════════ 主元件 ════════════════════════
export default function Blog() {
  const [view, setView] = useState('list');
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [currentPost, setCurrentPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [loading, setLoading] = useState(false);

  // 編輯狀態
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editCover, setEditCover] = useState('');
  const [editIsPublished, setEditIsPublished] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const textareaRef = useRef(null);

  // 留言狀態（用 localStorage 記暱稱、email 方便重複留言）
  const [cmtNickname, setCmtNickname] = useState(() => localStorage.getItem('wt_blog_nick') || '');
  const [cmtEmail, setCmtEmail] = useState(() => localStorage.getItem('wt_blog_email') || '');
  const [cmtContent, setCmtContent] = useState('');

  const admin = !!sessionStorage.getItem('wintime_admin_token');  // BLOG_ADMIN_KEY_FIX
  const adminParam = admin ? '?admin=1' : '';

  // ─── 載入文章列表 ───
  const loadList = useCallback(async (pageNum = 1, append = false) => {
    setLoading(true);
    try {
      const base = admin ? `${API}/api/blog/posts?admin=1&include_drafts=1&` : `${API}/api/blog/posts?`;
      const url = `${base}page=${pageNum}&limit=10`;
      const r = await fetch(url);
      const data = await r.json();
      if (data.ok) {
        setPosts(prev => append ? [...prev, ...(data.posts || [])] : (data.posts || []));
        setTotal(data.total || 0);
        setPage(pageNum);
      }
    } catch (e) {
      console.error('載入列表失敗', e);
    } finally { setLoading(false); }
  }, [admin]);

  // ─── 載入單篇 ───
  const loadSingle = useCallback(async (id) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/blog/posts/${id}${adminParam}`);
      const data = await r.json();
      if (data.ok) {
        setCurrentPost(data.post);
        setComments(data.comments || []);
        setView('single');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        alert('找不到這篇文章');
        setView('list');
      }
    } finally { setLoading(false); }
  }, [adminParam]);

  // ─── 開始新增 ───
  const startNew = () => {
    setEditingId(null);
    setEditTitle('');
    setEditContent('');
    setEditCover('');
    setEditIsPublished(true);
    setView('edit');
  };

  // ─── 開始編輯 ───
  const startEdit = () => {
    if (!currentPost) return;
    setEditingId(currentPost.id);
    setEditTitle(currentPost.title);
    setEditContent(currentPost.content);
    setEditCover(currentPost.cover_image || '');
    setEditIsPublished(currentPost.is_published);
    setView('edit');
  };

  // ─── 儲存（新增或編輯）───
  const savePost = async () => {
    if (!editTitle.trim() || !editContent.trim()) {
      alert('標題與內容都不能空白'); return;
    }
    const url = editingId
      ? `${API}/api/blog/posts/${editingId}?admin=1`
      : `${API}/api/blog/posts?admin=1`;
    const method = editingId ? 'PUT' : 'POST';
    try {
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editTitle.trim(),
          content: editContent,
          cover_image: editCover.trim() || null,
          is_published: editIsPublished,
        })
      });
      const data = await r.json();
      if (data.ok) {
        alert(editingId ? '已更新' : '已發布');
        setView('list');
        loadList();
      } else {
        alert('儲存失敗：' + (data.error || '未知錯誤'));
      }
    } catch (e) { alert('網路錯誤：' + e.message); }
  };

  // ─── 刪除文章 ───
  const deletePost = async () => {
    if (!window.confirm(`確定要刪除「${currentPost.title}」？\n留言也會一起刪掉，無法復原`)) return;
    try {
      const r = await fetch(`${API}/api/blog/posts/${currentPost.id}?admin=1`, { method: 'DELETE' });
      const data = await r.json();
      if (data.ok) {
        alert('已刪除');
        setView('list');
        loadList();
      }
    } catch (e) { alert('刪除失敗：' + e.message); }
  };

  // ─── 送出留言 ───
  const submitComment = async () => {
    if (!cmtNickname.trim() || !cmtContent.trim()) {
      alert('暱稱與留言內容必填'); return;
    }
    try {
      const r = await fetch(`${API}/api/blog/posts/${currentPost.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nickname: cmtNickname.trim(),
          email: cmtEmail.trim() || null,
          content: cmtContent.trim(),
        })
      });
      const data = await r.json();
      if (data.ok) {
        localStorage.setItem('wt_blog_nick', cmtNickname.trim());
        if (cmtEmail.trim()) localStorage.setItem('wt_blog_email', cmtEmail.trim());
        setCmtContent('');
        loadSingle(currentPost.id);
      } else if (data.error === 'rate_limited') {
        alert(data.message || '留言太頻繁，請稍候再試');
      } else {
        alert('留言失敗：' + (data.error || ''));
      }
    } catch (e) { alert('網路錯誤：' + e.message); }
  };

  // ─── 隱藏/顯示留言 ───
  const toggleHideComment = async (cid) => {
    await fetch(`${API}/api/blog/comments/${cid}/hide?admin=1`, { method: 'PUT' });
    loadSingle(currentPost.id);
  };

  // ─── 刪除留言 ───
  const deleteComment = async (cid) => {
    if (!window.confirm('確定刪除此留言？')) return;
    await fetch(`${API}/api/blog/comments/${cid}?admin=1`, { method: 'DELETE' });
    loadSingle(currentPost.id);
  };

  // ─── 管理員回覆留言 ───
  const submitReply = async (parentId) => {
    if (!replyContent.trim()) { alert('回覆內容必填'); return; }
    try {
      const r = await fetch(`${API}/api/blog/posts/${currentPost.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nickname: '站長',
          email: '',
          content: replyContent.trim(),
          parent_id: parentId
        })
      });
      const data = await r.json();
      if (data.ok) {
        setReplyTo(null);
        setReplyContent('');
        loadSingle(currentPost.id);
      } else {
        alert('回覆失敗：' + (data.error || ''));
      }
    } catch (e) {
      alert('回覆失敗：' + e.message);
    }
  };

  // ─── 圖片上傳（核心）───
  const uploadImage = async (file) => {
    if (!admin) { alert('只有管理員能上傳圖片'); return null; }
    if (!file.type.startsWith('image/')) { alert('只能上傳圖片檔'); return null; }
    if (file.size > 10 * 1024 * 1024) { alert('圖片不能超過 10MB'); return null; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/api/blog/upload-image?admin=1`, { method: 'POST', body: fd });
      const data = await r.json();
      if (data.ok) return data.url;
      alert('上傳失敗：' + (data.message || data.error || ''));
      return null;
    } catch (e) { alert('上傳網路錯誤：' + e.message); return null; }
    finally { setUploading(false); }
  };

  // ─── 圖片插入內文（編輯器用）───
  const insertImageToContent = async (file) => {
    const url = await uploadImage(file);
    if (!url) return;
    // 插入到游標位置
    const ta = textareaRef.current;
    const insertMd = `\n\n![圖片](${url})\n\n`;
    if (ta) {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const before = editContent.substring(0, start);
      const after = editContent.substring(end);
      setEditContent(before + insertMd + after);
      // 重新對焦
      setTimeout(() => {
        ta.focus();
        const pos = start + insertMd.length;
        ta.setSelectionRange(pos, pos);
      }, 0);
    } else {
      setEditContent(prev => prev + insertMd);
    }
  };

  // ─── 拖曳上傳 ───
  const onDropImage = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    for (const f of files) await insertImageToContent(f);
  };

  // ─── 貼上圖片（Ctrl+V）───
  const onPasteImage = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    // 找剪貼簿中第一個圖片類型的 item（截圖、複製檔案、複製網頁圖片都會走這）
    let imageFile = null;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type && items[i].type.startsWith('image/')) {
        imageFile = items[i].getAsFile();
        break;
      }
    }
    if (!imageFile) return;  // 沒圖片就讓 textarea 走正常貼上文字流程
    e.preventDefault();
    await insertImageToContent(imageFile);
  };

  // ─── 封面圖上傳 ───
  const onPickCover = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) setEditCover(url);
    e.target.value = '';
  };

  // ─── 內文圖片按鈕上傳 ───
  const onPickContentImage = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) await insertImageToContent(f);
    e.target.value = '';
  };

  // ─── 初始載入 ───
  useEffect(() => { if (view === 'list') loadList(); }, [view, loadList]);

  // ════════════════════════ 渲染：列表 view ════════════════════════
  if (view === 'list') return (
    <div style={S.page}>
      <div style={S.title}>WINTIME 筆記</div>
      <div style={S.subtitle}>NOTES & MARKET OBSERVATIONS</div>
      <div style={S.divider} />

      {admin && (
        <div style={{ marginBottom: 20 }}>
          <button style={S.btn('#1a6a96')} onClick={startNew}>+ 新增文章</button>
          <span style={{ fontSize: 11, color: '#4a6878', letterSpacing: 2, fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, marginLeft: 8 }}>
            管理員模式 · 含草稿
          </span>
        </div>
      )}

      {loading && <div style={S.empty}>載入中⋯</div>}
      {!loading && posts.length === 0 && <div style={S.empty}>還沒有文章</div>}

      {posts.map(p => (
        <div
          key={p.id}
          style={S.card}
          onClick={() => loadSingle(p.id)}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#4C9BB8'}
          onMouseLeave={e => e.currentTarget.style.borderColor = '#1a2a3c'}
        >
          <div style={S.cardTitle}>
            {p.title}
            {!p.is_published && admin && (
              <span style={{ marginLeft: 10, fontSize: 11, padding: '2px 8px', background: '#3a2a0a', color: '#f5c518', borderRadius: 3, letterSpacing: 1 }}>草稿</span>
            )}
          </div>
          <div style={S.cardMeta}>
            {(p.created_at || '').slice(0, 10)} · 瀏覽 {p.view_count}
          </div>
          <div style={S.cardExcerpt}>{stripMarkdown(p.excerpt)}{p.excerpt && p.excerpt.length >= 200 ? '⋯' : ''}</div>
          <div style={{ marginTop: 12, fontSize: 11, color: '#4C9BB8', fontFamily: "'Rajdhani',sans-serif", letterSpacing: 2, fontWeight: 700 }}>
            閱讀全文 →
          </div>
        </div>
      ))}

      {!loading && posts.length < total && (
        <div style={{ textAlign: 'center', marginTop: 20, marginBottom: 20 }}>
          <button style={S.btn('#1a6a96')} onClick={() => loadList(page + 1, true)}>
            載入更多文章（還有 {total - posts.length} 篇）
          </button>
        </div>
      )}
    </div>
  );

  // ════════════════════════ 渲染：單篇 view ════════════════════════
  if (view === 'single' && currentPost) return (
    <div style={S.page}>
      <button style={S.btn('#2a3a48')} onClick={() => setView('list')}>← 回列表</button>
      {admin && (
        <>
          <button style={S.btn('#4a6878')} onClick={startEdit}>編輯</button>
          <button style={S.btn('#8a2828')} onClick={deletePost}>刪除文章</button>
          {!currentPost.is_published && (
            <span style={{ marginLeft: 10, fontSize: 11, padding: '4px 10px', background: '#3a2a0a', color: '#f5c518', borderRadius: 3, letterSpacing: 1 }}>草稿（訪客看不到）</span>
          )}
        </>
      )}

      <div style={{ marginTop: 24 }}>
        <div style={S.postTitle}>{currentPost.title}</div>
        <div style={S.postMeta}>
          {(currentPost.created_at || '').slice(0, 16).replace('T', ' ')} · 瀏覽 {currentPost.view_count}
        </div>
        <div style={S.postBody}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{currentPost.content}</ReactMarkdown>
        </div>
      </div>

      {/* 留言區 */}
      <div style={{ marginTop: 30, paddingTop: 20, borderTop: '1px solid #1e2a3a' }}>
        <div style={{ ...S.label, fontSize: 13, letterSpacing: 4, marginBottom: 16 }}>
          💬 留言 ({comments.length})
        </div>

        {/* 留言輸入框 */}
        <div style={{ background: '#0a1219', border: '1px solid #1a2a3c', borderRadius: 6, padding: 16, marginBottom: 24 }}>
          <input
            value={cmtNickname}
            onChange={e => setCmtNickname(e.target.value)}
            placeholder="暱稱（必填）"
            maxLength={50}
            style={{ ...S.input, marginBottom: 10 }}
          />
          <textarea
            value={cmtContent}
            onChange={e => setCmtContent(e.target.value)}
            placeholder="寫下你的看法⋯"
            rows={3}
            maxLength={2000}
            style={{ ...S.textarea, marginBottom: 10, fontFamily: "'Barlow',sans-serif" }}
          />
          <button style={S.btn('#1a6a96')} onClick={submitComment}>送出留言</button>
          <span style={{ fontSize: 11, color: '#4a6878', marginLeft: 12, fontFamily: "'Rajdhani',sans-serif", letterSpacing: 1 }}>
            {cmtContent.length}/2000
          </span>
        </div>

        {/* 留言列表（巢狀：訪客留言 + 站長回覆縮排顯示） */}
        {comments.length === 0 && <div style={S.empty}>還沒有留言，當第一個吧</div>}
        {(() => {
          // 組樹：top-level (parent_id 為空) + replies (parent_id 指向 top-level)
          const topComments = comments.filter(c => !c.parent_id);
          const repliesByParent = {};
          comments.filter(c => c.parent_id).forEach(c => {
            (repliesByParent[c.parent_id] = repliesByParent[c.parent_id] || []).push(c);
          });
          return topComments.map(c => (
            <div key={c.id}>
              {/* 訪客原留言 */}
              <div style={{ ...S.commentBox, opacity: c.is_hidden ? 0.4 : 1 }}>
                <div style={S.commentMeta}>
                  {c.nickname}
                  {admin && c.email && <span style={{ color: '#4a6878', marginLeft: 8 }}>({c.email})</span>}
                  <span style={{ color: '#4a6878', marginLeft: 8 }}>· {(c.created_at || '').slice(5, 16).replace('T', ' ')}</span>
                  {c.is_hidden && <span style={{ color: '#f5c518', marginLeft: 8 }}>[已隱藏]</span>}
                </div>
                <div style={S.commentBody}>{c.content}</div>
                {admin && (
                  <div style={{ marginTop: 8 }}>
                    <button style={{ ...S.btn('#4a6878'), padding: '4px 12px', fontSize: 10 }} onClick={() => toggleHideComment(c.id)}>
                      {c.is_hidden ? '取消隱藏' : '隱藏'}
                    </button>
                    <button style={{ ...S.btn('#8a2828'), padding: '4px 12px', fontSize: 10 }} onClick={() => deleteComment(c.id)}>刪除</button>
                    <button style={{ ...S.btn('#7c6010'), padding: '4px 12px', fontSize: 10 }} onClick={() => { setReplyTo(c.id); setReplyContent(''); }}>回覆</button>
                  </div>
                )}
              </div>
              {/* 站長回覆們（縮排，金色框） */}
              {(repliesByParent[c.id] || []).map(reply => (
                <div key={reply.id} style={{ ...S.commentBox, marginLeft: 40, marginTop: 8, borderColor: '#7c6010', background: '#1a1610', opacity: reply.is_hidden ? 0.4 : 1 }}>
                  <div style={S.commentMeta}>
                    <span style={{ color: '#f5c518' }}>★ {reply.nickname}</span>
                    <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 8px', background: '#3a2a0a', color: '#f5c518', borderRadius: 3, letterSpacing: 1 }}>站長回覆</span>
                    <span style={{ color: '#4a6878', marginLeft: 8 }}>· {(reply.created_at || '').slice(5, 16).replace('T', ' ')}</span>
                    {reply.is_hidden && <span style={{ color: '#f5c518', marginLeft: 8 }}>[已隱藏]</span>}
                  </div>
                  <div style={S.commentBody}>{reply.content}</div>
                  {admin && (
                    <div style={{ marginTop: 8 }}>
                      <button style={{ ...S.btn('#4a6878'), padding: '4px 12px', fontSize: 10 }} onClick={() => toggleHideComment(reply.id)}>
                        {reply.is_hidden ? '取消隱藏' : '隱藏'}
                      </button>
                      <button style={{ ...S.btn('#8a2828'), padding: '4px 12px', fontSize: 10 }} onClick={() => deleteComment(reply.id)}>刪除</button>
                    </div>
                  )}
                </div>
              ))}
              {/* 回覆輸入框（管理員專屬，點「回覆」按鈕後出現） */}
              {admin && replyTo === c.id && (
                <div style={{ marginLeft: 40, marginTop: 8, padding: 12, background: '#0f1820', border: '1px solid #3a2d10', borderRadius: 6 }}>
                  <textarea
                    value={replyContent}
                    onChange={e => setReplyContent(e.target.value)}
                    placeholder={`回覆 @${c.nickname}⋯`}
                    rows={2}
                    maxLength={2000}
                    style={{ ...S.textarea, marginBottom: 8, fontFamily: "'Barlow',sans-serif" }}
                    autoFocus
                  />
                  <button style={{ ...S.btn('#1a6a96'), padding: '5px 14px', fontSize: 11 }} onClick={() => submitReply(c.id)}>送出回覆</button>
                  <button style={{ ...S.btn('#4a6878'), padding: '5px 14px', fontSize: 11 }} onClick={() => { setReplyTo(null); setReplyContent(''); }}>取消</button>
                  <span style={{ fontSize: 10, color: '#4a6878', marginLeft: 10, fontFamily: "'Rajdhani',sans-serif" }}>
                    站長身份 · {replyContent.length}/2000
                  </span>
                </div>
              )}
            </div>
          ));
        })()}
      </div>
    </div>
  );

  // ════════════════════════ 渲染：編輯 view ════════════════════════
  if (view === 'edit') return (
    <div style={S.page}>
      <div style={S.title}>{editingId ? '編輯文章' : '新增文章'}</div>
      <div style={S.subtitle}>{editingId ? `EDITING #${editingId}` : 'NEW POST'}</div>
      <div style={S.divider} />

      {/* 標題 */}
      <label style={S.label}>標題</label>
      <input
        value={editTitle}
        onChange={e => setEditTitle(e.target.value)}
        placeholder="文章標題"
        maxLength={300}
        style={{ ...S.input, fontSize: 18, fontFamily: "'Rajdhani',sans-serif", fontWeight: 700 }}
      />

      {/* 工具列 */}
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
        <label style={{ ...S.label, marginBottom: 0 }}>內容（Markdown）</label>
        <button style={{ ...S.btn('#4a6878'), padding: '4px 12px', fontSize: 10, marginRight: 0 }} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
          {uploading ? '上傳中⋯' : '📷 插入圖片'}
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onPickContentImage} />
        <span style={{ fontSize: 10, color: '#4a6878', fontFamily: "'Rajdhani',sans-serif", letterSpacing: 1 }}>
          也可直接拖曳圖片到下方輸入區
        </span>
      </div>

      {/* 編輯區（左）+ 預覽區（右）並排 */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <textarea
            ref={textareaRef}
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            onDrop={onDropImage}
            onPaste={onPasteImage}
            onDragOver={e => e.preventDefault()}
            placeholder="用 Markdown 寫內容...&#10;&#10;# 標題1&#10;## 標題2&#10;**粗體** *斜體*&#10;- 列表&#10;[連結文字](https://...)&#10;![圖片描述](圖片網址)&#10;&#10;直接拖圖片進來會自動上傳並插入"
            rows={28}
            style={S.textarea}
          />
        </div>
        <div style={{ flex: 1, background: '#080d14', border: '1px solid #1a2a3c', borderRadius: 4, padding: '14px 18px', overflowY: 'auto', maxHeight: 700 }}>
          <div style={{ ...S.label, marginBottom: 12, color: '#4a6878' }}>即時預覽</div>
          {editContent.trim()
            ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{editContent}</ReactMarkdown>
            : <div style={{ color: '#4a6878', fontStyle: 'italic', fontSize: 13 }}>（預覽會顯示在這裡）</div>
          }
        </div>
      </div>

      {/* Markdown 速查 */}
      <details style={{ marginBottom: 16, padding: 12, background: '#0a1219', border: '1px solid #1a2a3c', borderRadius: 4 }}>
        <summary style={{ cursor: 'pointer', color: '#90c0dc', fontFamily: "'Rajdhani',sans-serif", fontSize: 12, letterSpacing: 2, fontWeight: 700 }}>📖 Markdown 語法速查</summary>
        <div style={{ marginTop: 10, fontSize: 12, fontFamily: "'JetBrains Mono',monospace", color: '#90c0dc', lineHeight: 2 }}>
          <div><code style={{ color: '#7ab0cc' }}># 大標題</code> &nbsp;&nbsp; <code style={{ color: '#7ab0cc' }}>## 中標題</code> &nbsp;&nbsp; <code style={{ color: '#7ab0cc' }}>### 小標題</code></div>
          <div><code style={{ color: '#7ab0cc' }}>**粗體文字**</code> &nbsp;&nbsp; <code style={{ color: '#7ab0cc' }}>*斜體文字*</code></div>
          <div><code style={{ color: '#7ab0cc' }}>- 列表項目</code> &nbsp;&nbsp; <code style={{ color: '#7ab0cc' }}>1. 編號列表</code></div>
          <div><code style={{ color: '#7ab0cc' }}>[連結文字](https://example.com)</code></div>
          <div><code style={{ color: '#7ab0cc' }}>![圖片描述](圖片網址)</code> ← 拖曳上傳會自動產生這行</div>
          <div><code style={{ color: '#7ab0cc' }}>&gt; 引言文字</code> &nbsp;&nbsp; <code style={{ color: '#7ab0cc' }}>---</code> ← 分隔線</div>
        </div>
      </details>

      {/* 發布狀態 */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
        <input type="checkbox" checked={editIsPublished} onChange={e => setEditIsPublished(e.target.checked)} />
        <span style={{ color: '#cfe6f5', fontFamily: "'Rajdhani',sans-serif", fontSize: 13, letterSpacing: 1 }}>
          立即發布（取消勾選則為草稿，只有管理員看得到）
        </span>
      </label>

      {/* 動作按鈕 */}
      <div>
        <button style={S.btn('#1a6a96')} onClick={savePost}>{editingId ? '更新文章' : '發布文章'}</button>
        <button style={S.btn('#2a3a48')} onClick={() => editingId ? setView('single') : setView('list')}>取消</button>
      </div>
    </div>
  );

  return <div style={S.page}><div style={S.empty}>載入中⋯</div></div>;
}
